import { keepPreviousData, queryOptions, useQuery } from "@tanstack/react-query";
import {
  fetchEfficiencyHistory,
  fetchOdometerHistory,
  fetchStateOfChargeHistory,
  type HistoryWindow
} from '../api/vehicle.service'

type HistoryFetcher<T> = (
  vin: string,
  window?: HistoryWindow,
  signal?: AbortSignal
) => Promise<T>

// To prevent a lot of queries, we're going to have a minimal query time
// for each call
const MIN_FETCH_MS = 300

/** Shared query options for any bucketed vehicle history endpoint. */
const historyQueryOptions = <T>(
  segment: string,
  fetcher: HistoryFetcher<T>,
  vin: string,
  window: HistoryWindow
) =>
  queryOptions({
    queryKey: [
      'vehicles',
      vin,
      'history',
      segment,
      window.range ?? null,
      window.from?.toISOString() ?? null,
      window.to?.toISOString() ?? null
    ],
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const [data] = await Promise.all([
        fetcher(vin, window, signal),
        new Promise(resolve => setTimeout(resolve, MIN_FETCH_MS))
      ])
      return data
    }
  })

/** Historic odometer increases over the given look-back window. */
export const useOdometerHistory = (vin: string, window: HistoryWindow) =>
  useQuery(historyQueryOptions('odometer', fetchOdometerHistory, vin, window))

/** Historic state-of-charge over the given look-back window. */
export const useStateOfChargeHistory = (vin: string, window: HistoryWindow) =>
  useQuery(historyQueryOptions('state-of-charge', fetchStateOfChargeHistory, vin, window))

/** Historic energy efficiency over the given look-back window. */
export const useEfficiencyHistory = (vin: string, window: HistoryWindow) =>
  useQuery(historyQueryOptions('efficiency', fetchEfficiencyHistory, vin, window))
